import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import PageSizeDropdown from './PageSizeDropdown';
import { DateTime } from 'luxon';
import { useAuth } from '@zones-digital-platform/react-oidc-auth';
import "../css/spinner.css";
import axios from "axios";
// import { Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Toolbar } from '@mui/material';
import { Menu as MenuIcon, Home as HomeIcon, Inventory2 as InventoryIcon, Search as SearchIcon, Logout as LogoutIcon, Clear as ClearIcon } from '@mui/icons-material';

import {
    Box,
    CssBaseline,
    TextField,
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    CircularProgress,
    Container,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
const theme = createTheme({
    typography: {
      fontSize: 16, 
    },
});
const APP_BAR_HEIGHT = 64;

const sumUrl = process.env.REACT_APP_INTERNAL_SUM;
const dataApi = process.env.REACT_APP_DATA_FIELD;
const pageTitle = process.env.REACT_APP_ENV_TITLE;

const Home = () => {

    // console.log(azureEnvVar ? azureEnvVar : 'Azure Environment Variable REACT_APP_AZURE_ENV_VAR not set.'); // test azure env variable
  const headers = [
    { key: "poNumber", label: "PO Number" },
    { key: "createdDate", label: "Created Date" },
    { key: "customer", label: "Customer" },
    { key: "shipTo", label: "Ship To" },
    { key: "overallStatus", label: "Status" },
  ];

    const columns = headers.map((header) => ({
        field: header.key,
        headerName: header.label,
        flex: 1,
        sortable: true,
        renderCell: (params) => {
          if (header.key === "poNumber") {
            return <Link to={`poview/${params.row.id}`}>{params.value}</Link>;
          }
          if (header.key === "createdDate") {
            return DateTime.fromISO(params.value).toFormat("MMM dd t");
          }
          if (header.key === "customer") {
            return `${params.row.extCustomerId} - ${params.row.extCustomerMappingId}`;
          }
          return params.value;
        },
    }));

    const [allOrders, setAllOrders] = useState([]); // Store all orders
    const [filteredOrders, setFilteredOrders] = useState([]); // Store filtered orders
    const [sortColumn, setSortColumn] = useState("createdDate");
    const [sortOrder, setSortOrder] = useState("desc");
    const [searchQuery, setSearchQuery] = useState("");
    const [pageSize, setPageSize] = useState(1000);
    const [loading, setLoading] = useState(true);
    const [navOpen, setNavOpen] = useState(false);
    const itemsPerPage = 500;
    const [currentPage, setCurrentPage] = useState(1);
    const { isAuthenticated, logout, getToken } = useAuth();

    const handleLogout = async () => {
        try {
            await logout(); // Call the logout function from useAuth
            // The library should handle redirection to postLogoutRedirectUri (/login)
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const handleSort = (key) => {
        if (sortColumn === key) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(key);
            setSortOrder("asc");
        }
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const handlePageSizeChange = (event) => {
        const newSize = parseInt(event.target.value, 10);
        setPageSize(newSize);
    };

    useEffect(() => {
        // Fetch all orders initially
        const fetchOrders = async () => {
            try {
                const response = await fetch(sumUrl,
                    {
                        headers: {
                            'Authorization': `Bearer ${getToken()}`
                        }
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                const orders = dataApi ? data[dataApi] : data;
                setAllOrders(orders);
                setFilteredOrders(orders);
            } catch (error) {
                console.error("Error fetching orders:", error);
            }
        };
        if (isAuthenticated) {

            fetchOrders();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        // Apply search filtering whenever searchQuery or allOrders changes
        const filtered = allOrders.filter((order) =>
            Object.values(order)
                .join(" ")
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
        );
        setFilteredOrders(filtered);
    }, [searchQuery, allOrders]);

    useEffect(() => {
        document.title = 'Global API - Orders - ' + pageTitle;
    }, []);

    const sortedAndLimitedOrders = filteredOrders
        .sort((a, b) => {
            const order = sortOrder === "asc" ? 1 : -1;
            return a[sortColumn] < b[sortColumn] ? -order : order;
        })
        .slice(0, pageSize);

    useEffect(() => {
        if (Object.keys(filteredOrders).length === 0) {
            setLoading(true);
            const timer = setTimeout(() => {
                setLoading(false);
            }, 2000); // Set the timeout duration in milliseconds (e.g., 3000ms for 3 seconds)

            return () => clearTimeout(timer); // Cleanup the timer on component unmount
        } else {
            setLoading(false);
        }
    }, [filteredOrders]);

    const clearSearch = () => {
        setSearchQuery('');
    };

    const totalPages = Math.ceil(sortedAndLimitedOrders.length / itemsPerPage);

    // Calculate the current items
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedAndLimitedOrders.slice(indexOfFirstItem, indexOfLastItem);

    // Handle page change
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Handle next and previous buttons
    const handleNext = () => {
        if (currentPage < totalPages) {
            setCurrentPage((prevPage) => prevPage + 1);
        }
    };

    const handlePrevious = () => {
        if (currentPage > 1) {
            setCurrentPage((prevPage) => prevPage - 1);
        }
    };

    if (!isAuthenticated) {
        return <div className="spinner-container">
            <div className="loading-spinner">
            </div>
        </div>;
    }

    return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Box sx={{ flexGrow: 1, overflowX: "hidden" }}>
            {/* App Bar */}
            <AppBar position="static" sx={{ backgroundColor: "#1976d2" }}>
              <Toolbar>
                <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                  <IconButton
                    color="inherit"
                    edge="start"
                    onClick={() => setNavOpen(true)}
                    sx={{ mr: 1 }}
                  >
                    <MenuIcon />
                  </IconButton>
                  <img
                    src={require("../images/zones_logo_white.png")}
                    style={{ height: "20px", marginRight: "15px" }}
                    alt="Zones Logo"
                  />
                  <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                    Global Purchase Orders
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1, justifyContent: "flex-end" }}>
                  <IconButton color="inherit" onClick={handleLogout} sx={{ ml: 1 }}>
                    <LogoutIcon />
                  </IconButton>
                </Box>
              </Toolbar>
            </AppBar>
    
            {/* Drawer */}
            <Drawer
              anchor="left"
              open={navOpen}
              onClose={() => setNavOpen(false)}
              ModalProps={{ keepMounted: true }}
              sx={{
                "& .MuiDrawer-paper": {
                  width: 260,
                  top: APP_BAR_HEIGHT,
                  height: `calc(100% - ${APP_BAR_HEIGHT}px)`,
                },
              }}
            >
              <List>
                <ListItemButton
                  component={Link}
                  to="/"
                  onClick={() => setNavOpen(false)}
                >
                  <ListItemIcon>
                    <HomeIcon />
                  </ListItemIcon>
                  <ListItemText primary="Orders" />
                </ListItemButton>
                <ListItemButton
                  component={Link}
                  to="/partnerInventory"
                  onClick={() => setNavOpen(false)}
                >
                  <ListItemIcon>
                    <InventoryIcon />
                  </ListItemIcon>
                  <ListItemText primary="Partner Inventory" />
                </ListItemButton>
              </List>
            </Drawer>
    
            {/* Main Content */}
            <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
                <Box
                    sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                        <TextField
                            placeholder="Search"
                            value={searchQuery}
                            onChange={handleSearch}
                            InputProps={{
                            startAdornment: (
                                <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                            ),
                            endAdornment: searchQuery ? (
                                <IconButton
                                size="small"
                                onClick={clearSearch}
                                aria-label="clear search"
                                >
                                <ClearIcon fontSize="small" />
                                </IconButton>
                            ) : null,
                            }}
                            sx={{
                            minWidth: 300,
                            "& .MuiOutlinedInput-root": {
                                fontSize: "1rem",
                            },
                            }}
                            variant="outlined"
                            size="small"
                        />
                    </Box>
                </Box>
                <Paper sx={{ height: "78vh", width: "100%", overflow: "auto" }}>
                    {loading ? (
                    <Box
                        sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        }}
                    >
                        <CircularProgress />
                    </Box>
                    ) : (
                    <DataGrid
                        rows={currentItems}
                        columns={columns}
                        pageSize={pageSize}
                        rowsPerPageOptions={[100, 200, 500, 1000]}
                        pagination
                        page={currentPage - 1} 
                        onPageChange={(newPage) => handlePageChange(newPage + 1)}
                        rowCount={totalPages * pageSize} 
                        paginationMode="server"
                        sortingMode="server"
                        onSortModelChange={(model) => {
                        if (model.length > 0) {
                            handleSort(model[0].field);
                        }
                        }}
                        disableSelectionOnClick
                        sx={{
                        "& .MuiDataGrid-cell:focus": {
                            outline: "none",
                        },
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: "#f5f5f5",
                        },
                        }}
                    />
                    )}
                </Paper>
        
                {/* Pagination Buttons - REMOVED because grid has pagination built in */}
                {/* <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
                    <Button
                    variant="outlined"
                    onClick={handlePrevious}
                    disabled={currentPage === 1}
                    sx={{ textTransform: "none" }}
                    >
                    Previous
                    </Button>
                    {Array.from({ length: totalPages }, (_, index) => (
                    <Button
                        key={index + 1}
                        variant={currentPage === index + 1 ? "contained" : "outlined"}
                        onClick={() => handlePageChange(index + 1)}
                        sx={{
                        minWidth: 40,
                        backgroundColor:
                            currentPage === index + 1 ? "#1976d2" : "transparent",
                        "&:hover": {
                            backgroundColor:
                            currentPage === index + 1 ? "#1565c0" : "#f5f5f5",
                        },
                        }}
                    >
                        {index + 1}
                    </Button>
                    ))}
                    <Button
                    variant="outlined"
                    onClick={handleNext}
                    disabled={currentPage === totalPages}
                    sx={{ textTransform: "none" }}
                    >
                    Next
                    </Button>
                </Box> */}
            </Container>
          </Box>
        </ThemeProvider>
    );
};

export default Home;